import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {COLORS} from '../../../constants/colors';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {ProductImage} from '../ProductImage/ProductImage';
import {bannerWideAndUpLinaria} from './bannerContainer';

export interface BannerProductImage {
  src: string;
  alt?: string;
}

type Size = 'highlight' | 'sponsoredBrand';

// 30 mobile, 36 desktop — used as the source-set hint for `<ProductImage>`.
const HIGHLIGHT_SRC_WIDTH = 36;
// 44 mobile, 84 desktop — pick the larger as the source-set hint.
const SPONSORED_BRAND_SRC_WIDTH = 84;

// 30×40, 36×48, 44×58 — all ≈ 3:4. Reuse the ProductImage default ratio.
const TILE_RATIO = '3:4';

interface BannerProductImagesProps {
  products: BannerProductImage[];
  size: Size;
  className?: string;
  ['data-testid']?: string;
}

export const BannerProductImages: FC<BannerProductImagesProps> = ({
  products,
  size,
  className,
  'data-testid': dataTestid = 'promotionBanner-productImages',
}) => {
  if (products.length === 0) return null;

  const srcWidth =
    size === 'highlight' ? HIGHLIGHT_SRC_WIDTH : SPONSORED_BRAND_SRC_WIDTH;

  return (
    <Strip className={className} data-testid={dataTestid} data-size={size}>
      {products.map((image, idx) => (
        <TileSlot key={`${image.src}-${idx}`} data-size={size}>
          <ProductImage
            src={image.src}
            alt={image.alt ?? ''}
            ratio={TILE_RATIO}
            breakpoints={[{width: `${srcWidth}px`}]}
            padded
            loading="lazy"
          />
        </TileSlot>
      ))}
      <ArrowTile data-arrow="true" data-size={size}>
        <ArrowIconBox data-size={size}>
          <Icon
            asset={GenericIconName.ArrowLongRight}
            width="100%"
            height="100%"
            fill={COLORS.Black}
          />
        </ArrowIconBox>
      </ArrowTile>
    </Strip>
  );
};

const Strip = styled.div`
  display: inline-flex;
  align-items: center;

  &[data-size='highlight'] {
    gap: 3px;

    ${bannerWideAndUpLinaria(`
      gap: 4px;
    `)}
  }

  &[data-size='sponsoredBrand'] {
    gap: 4px;

    ${bannerWideAndUpLinaria(`
      gap: 8px;
    `)}
  }
`;

const TileSlot = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  position: relative;

  &[data-size='highlight'] {
    width: 30px;
    height: 40px;

    ${bannerWideAndUpLinaria(`
      width: 36px;
      height: 48px;
    `)}
  }

  &[data-size='sponsoredBrand'] {
    width: 44px;
    height: 58px;

    // ASFE-14429: the compact (mobile / narrow-column) sponsored-content banner
    // shows at most two product tiles — the arrow tile then stands in for the
    // rest. Mirrors the legacy HeaderBanner SponsoredBanner (which slices to
    // two on mobile), but keyed off the banner's OWN width via the container
    // query, so a narrow desktop column (e.g. the basket) gets the same limit.
    &:nth-child(n + 3) {
      display: none;
    }

    ${bannerWideAndUpLinaria(`
      width: 84px;
      height: 112px;

      &:nth-child(n + 3) {
        display: inline-flex;
      }
    `)}
  }
`;

const ArrowTile = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${COLORS['Fill.Grey.Primary']};
  border-radius: 4px;
  flex-shrink: 0;

  &[data-size='highlight'] {
    width: 30px;
    height: 40px;

    ${bannerWideAndUpLinaria(`
      width: 36px;
      height: 48px;
    `)}
  }

  &[data-size='sponsoredBrand'] {
    width: 44px;
    height: 58px;

    ${bannerWideAndUpLinaria(`
      width: 84px;
      height: 112px;
    `)}
  }
`;

const ArrowIconBox = styled.span`
  display: inline-flex;
  flex-shrink: 0;

  &[data-size='highlight'] {
    width: 12px;
    height: 12px;

    ${bannerWideAndUpLinaria(`
      width: 14px;
      height: 14px;
    `)}
  }

  &[data-size='sponsoredBrand'] {
    width: 18px;
    height: 18px;

    ${bannerWideAndUpLinaria(`
      width: 24px;
      height: 24px;
    `)}
  }
`;
